import React from "react";
import Pagination from "../../../../components/Pagination";
import { STATUS } from "../utils/constants";

/**
 * Desktop table view component for product availability
 * Displays products in a table with sticky header and pagination
 */
const AvailabilityTable = ({
  products,
  currencySymbol,
  page,
  totalPages,
  onPageChange,
}) => {
  /**
   * Get status badge component
   * @param {string} status - Product status
   * @returns {JSX.Element} Status badge
   */
  const getStatusBadge = (status) => {
    const isAvailable = status === STATUS.AVAILABLE;

    return (
      <span
        className={`font-bold text-sm ${
          isAvailable ? "text-green-600" : "text-red-600"
        }`}
      >
        {status}
      </span>
    );
  };

  /**
   * Format price with currency symbol
   * @param {number|string} price - Price value
   * @returns {string} Formatted price
   */
  const formatPrice = (price) => {
    return price !== "-" ? `${currencySymbol}${price}` : "-";
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-black sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Product Number
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Product Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Subcategory
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">
                Offer Price
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                  No products found.
                </td>
              </tr>
            ) : (
              products.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {item.productNumber}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {item.productName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.categoryName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.subcategoryName}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600">
                    {formatPrice(item.price)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-red-600">
                    {formatPrice(item.offerPrice)}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {getStatusBadge(item.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-gray-100 px-6 py-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default AvailabilityTable;
